import * as React from 'react';
import {tokens} from '@packages/utilities/styled';
import {usePrefersReducedMotion} from '@packages/systems/spring-hooks';

import type {SpringTooling} from '@packages/systems/spring';

export type AnimatedSiteBuilderSkeletonProps = {
  /**
   * Human-readable name for the SVG. When set, the graphic is exposed to
   * assistive tech; when omitted, the image is `aria-hidden`.
   */
  title?: string;
  /**
   * Fill color for the outer card. Defaults to transparent (undefined),
   * matching AI Site Builder's usage where the surrounding surface already
   * provides the background.
   */
  cardBackground?: string;
} & React.SVGProps<SVGSVGElement> &
  SpringTooling;

/**
 * Animated page-layout skeleton (wireframe) used in AI site builder–related flows.
 *
 * @component
 */
export const AnimatedSiteBuilderSkeleton = React.forwardRef<
  SVGSVGElement,
  AnimatedSiteBuilderSkeletonProps
>(({className, title, cardBackground, ...props}, ref) => {
  const baseId = React.useId().replaceAll(':', '');
  const scope = `asb-skel-${baseId}`;
  const shimmerId = `asb-shimmer-${baseId}`;

  const reduceMotion = usePrefersReducedMotion();

  const styleContent = `
    .${scope} .asb-logo,
    .${scope} .asb-nav-item-1,
    .${scope} .asb-nav-item-2,
    .${scope} .asb-nav-item-3,
    .${scope} .asb-nav-item-4 {
      animation: asb_fade_out_in_10s 10s ease-in-out infinite;
    }

    @keyframes asb_fade_out_in_10s {
      0%, 80%, 100% { opacity: 1; }
      90% { opacity: 0; }
    }

    .${scope} .asb-capital-a-letter,
    .${scope} .asb-lowercase-a-letter,
    .${scope} .asb-text-line-1,
    .${scope} .asb-text-line-2,
    .${scope} .asb-text-line-3,
    .${scope} .asb-button {
      transform-box: fill-box;
      transform-origin: left center;
    }

    .${scope} .asb-capital-a-letter,
    .${scope} .asb-lowercase-a-letter {
      animation:
        asb_fade_out_in_10s 10s ease-in-out infinite,
        asb_shift_left_letters_10s 10s ease-in-out infinite;
    }

    .${scope} .asb-letter-pair-default,
    .${scope} .asb-letter-pair-cursive {
      transform-box: fill-box;
      transform-origin: center center;
    }

    .${scope} .asb-letter-pair-default {
      animation: asb_letter_variant_default_20s 20s ease-in-out infinite
        backwards;
    }

    /* Match @keyframes 0% so first paint does not flash full-opacity cursive */
    .${scope} .asb-letter-pair-cursive {
      opacity: 0;
      transform: translateY(-16px);
      animation: asb_letter_variant_cursive_20s 20s ease-in-out infinite
        backwards;
    }

    /* Letter crossfade (46–50% and 96–100% of 20s): outgoing fades up, incoming rises from below */
    @keyframes asb_letter_variant_default_20s {
      0%, 46% { opacity: 1; transform: translateY(0); }
      50% { opacity: 0; transform: translateY(-16px); }
      96% { opacity: 0; transform: translateY(16px); }
      100% { opacity: 1; transform: translateY(0); }
    }

    @keyframes asb_letter_variant_cursive_20s {
      0% { opacity: 0; transform: translateY(-16px); }
      46% { opacity: 0; transform: translateY(16px); }
      50% { opacity: 1; transform: translateY(0); }
      96% { opacity: 1; transform: translateY(0); }
      100% { opacity: 0; transform: translateY(-16px); }
    }

    .${scope} .asb-text-line-1,
    .${scope} .asb-text-line-2,
    .${scope} .asb-text-line-3 {
      animation:
        asb_fade_out_in_10s 10s ease-in-out infinite,
        asb_shift_left_text_10s 10s ease-in-out infinite;
    }

    .${scope} .asb-button {
      animation:
        asb_fade_out_in_10s 10s ease-in-out infinite,
        asb_shift_left_button_10s 10s ease-in-out infinite;
    }

    @keyframes asb_shift_left_letters_10s {
      0%, 45%, 100% { transform: translateX(0); }
      50%, 95% { transform: translateX(-100px); }
    }

    @keyframes asb_shift_left_text_10s {
      0%, 45%, 100% { transform: translateX(0) scaleX(1); }
      50%, 95% { transform: translateX(-36px) scaleX(0.6); }
    }

    @keyframes asb_shift_left_button_10s {
      0%, 45%, 100% { transform: translateX(0); }
      50%, 95% { transform: translateX(-96px); }
    }

    .${scope} .asb-aside-wrapper {
      opacity: 0;
      animation: asb_aside_fade_in_10s 10s ease-in-out infinite;
    }

    @keyframes asb_aside_fade_in_10s {
      0%, 50%, 100% { opacity: 0; }
      55%, 80% { opacity: 1; }
      90% { opacity: 0; }
    }

    .${scope} .asb-sun-1,
    .${scope} .asb-sun-2,
    .${scope} .asb-mountains-1,
    .${scope} .asb-mountains-2 {
      transform-box: fill-box;
      transform-origin: center;
      animation: asb_image_content_cycle_10s 10s ease-in-out infinite;
    }

    @keyframes asb_image_content_cycle_10s {
      0% { opacity: 0; transform: translateY(16px); }
      8%, 65% { opacity: 1; transform: translateY(0); }
      70%, 100% { opacity: 0; transform: translateY(16px); }
    }

    .${scope} .asb-small-text-block {
      opacity: 0;
      transform-box: fill-box;
      transform-origin: center;
      animation: asb_small_text_block_cycle_10s 10s ease-in-out infinite;
    }

    @keyframes asb_small_text_block_cycle_10s {
      0%, 70% { opacity: 0; transform: translateY(-8px); }
      75%, 90% { opacity: 1; transform: translateY(0); }
      95%, 100% { opacity: 0; transform: translateY(-8px); }
    }

    @media (prefers-reduced-motion: reduce) {
      .${scope} * {
        animation: none !important;
      }

      /* These start hidden/offset as their pre-animation resting state, not
         a mid-cycle frame — forcing opacity/transform to visible here would
         show both letter variants, the aside panel, etc. all at once. */
      .${scope} .asb-letter-pair-cursive,
      .${scope} .asb-aside-wrapper,
      .${scope} .asb-small-text-block {
        opacity: 0 !important;
      }
    }
  `;

  return (
    <svg
      aria-hidden={title ? undefined : true}
      aria-label={title}
      className={scope + (className ? ` ${className}` : '')}
      fill="none"
      height="640"
      ref={ref}
      role={title ? 'img' : 'presentation'}
      viewBox="0 0 560 640"
      width="560"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {title ? <title>{title}</title> : null}
      <defs>
        <style>{styleContent}</style>
        <linearGradient
          gradientTransform="matrix(1,0,0,1,0,0)"
          gradientUnits="userSpaceOnUse"
          id={shimmerId}
          x1="-560"
          x2="0"
          y1="0"
          y2="0"
        >
          <stop offset="0%" stopColor={tokens.colors.background3} />
          <stop offset="45%" stopColor={tokens.colors.background3} />
          <stop offset="50%" stopColor={tokens.colors.grayBackground} />
          <stop offset="55%" stopColor={tokens.colors.background3} />
          <stop offset="100%" stopColor={tokens.colors.background3} />
          {reduceMotion ? null : (
            <animateTransform
              attributeName="gradientTransform"
              dur="1.8s"
              from="-560 0"
              repeatCount="indefinite"
              to="1120 0"
              type="translate"
            />
          )}
        </linearGradient>
      </defs>
      <rect
        fill={cardBackground}
        height="498"
        rx="11"
        stroke={tokens.colors.borderDefault}
        strokeWidth="2"
        width="378"
        x="91"
        y="71"
      />
      <rect
        height="50"
        rx="7"
        stroke={tokens.colors.borderDefault}
        strokeWidth="2"
        width="330"
        x="115"
        y="95"
      />
      <g fill={`url(#${shimmerId})`}>
        <rect
          className="asb-logo"
          height="28"
          rx="4"
          width="28"
          x="126"
          y="106"
        >
          {reduceMotion ? null : (
            <animate
              attributeName="rx"
              calcMode="spline"
              dur="4s"
              keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
              keyTimes="0;0.5;1"
              repeatCount="indefinite"
              values="4;20;4"
            />
          )}
        </rect>
        <rect
          className="asb-nav-item-1"
          height="10"
          rx="4"
          width="40"
          x="164"
          y="115"
        />
        <rect
          className="asb-nav-item-2"
          height="10"
          rx="4"
          width="40"
          x="294"
          y="115"
        />
        <rect
          className="asb-nav-item-3"
          height="10"
          rx="4"
          width="40"
          x="344"
          y="115"
        />
        <rect
          className="asb-nav-item-4"
          height="10"
          rx="4"
          width="40"
          x="394"
          y="115"
        />
        <g className="asb-letter-pair-default">
          <path
            className="asb-capital-a-letter"
            d="M259.728 202H269.447L284.065 242.423H275.551L261.645 202.934H267.174L253.322 242.423H245L259.728 202ZM253.651 226.276H275.551V233.114H253.651V226.276Z"
          />
          <path
            className="asb-lowercase-a-letter"
            d="M297.808 243C295.052 243 292.588 242.313 290.417 240.94C288.263 239.567 286.566 237.691 285.325 235.311C284.102 232.912 283.49 230.203 283.49 227.182C283.509 224.161 284.147 221.461 285.407 219.081C286.666 216.683 288.391 214.815 290.581 213.479C292.771 212.142 295.253 211.483 298.027 211.502C299.816 211.502 301.531 211.85 303.174 212.545C304.816 213.223 306.194 214.184 307.307 215.429C308.421 216.655 309.114 218.028 309.388 219.548L307.499 219.466V212.161H315V242.423H307.554L307.581 236.107L309.607 235.009C309.132 236.547 308.284 237.929 307.061 239.155C305.838 240.364 304.406 241.307 302.763 241.984C301.139 242.661 299.487 243 297.808 243ZM299.259 236.217C300.828 236.217 302.261 235.833 303.557 235.064C304.871 234.276 305.902 233.205 306.65 231.851C307.417 230.478 307.809 228.921 307.828 227.182C307.828 225.461 307.444 223.923 306.678 222.569C305.929 221.196 304.898 220.125 303.584 219.356C302.27 218.587 300.828 218.202 299.259 218.202C297.708 218.202 296.321 218.578 295.098 219.328C293.893 220.061 292.953 221.113 292.278 222.486C291.603 223.859 291.265 225.425 291.265 227.182C291.265 228.958 291.603 230.523 292.278 231.878C292.953 233.233 293.893 234.295 295.098 235.064C296.321 235.833 297.708 236.217 299.259 236.217Z"
          />
        </g>
        <g className="asb-letter-pair-cursive">
          <path
            className="asb-capital-a-letter"
            d="M245 242.153V240.883L246.647 240.4C247.501 240.158 248.152 239.795 248.599 239.311C249.087 238.827 249.473 238.162 249.758 237.316L262.079 202H267.142L279.403 237.558C279.728 238.444 280.094 239.13 280.501 239.614C280.908 240.057 281.558 240.42 282.453 240.702L283.368 240.944V242.153H267.874V240.944L268.911 240.642C269.806 240.359 270.335 239.916 270.497 239.311C270.701 238.706 270.66 237.961 270.375 237.074L267.813 229.273H254.333L251.71 236.953C251.425 237.84 251.344 238.565 251.466 239.13C251.588 239.694 252.096 240.138 252.991 240.46L254.333 240.883V242.153H245ZM254.943 227.519H267.264L261.286 209.317L254.943 227.519Z"
          />
          <path
            className="asb-lowercase-a-letter"
            d="M293.895 243C291.577 243 289.604 242.355 287.978 241.065C286.392 239.735 285.599 237.84 285.599 235.381C285.599 233.445 286.534 231.672 288.405 230.059C290.316 228.406 293.305 227.197 297.372 226.431C298.063 226.269 298.856 226.128 299.75 226.007C300.686 225.846 301.601 225.685 302.495 225.524V221.23C302.495 218.489 302.15 216.594 301.458 215.546C300.767 214.498 299.669 213.973 298.165 213.973H297.982C297.046 213.973 296.294 214.276 295.725 214.881C295.196 215.445 294.85 216.392 294.688 217.723L294.505 218.509C294.383 220.121 293.956 221.311 293.224 222.077C292.492 222.802 291.577 223.165 290.479 223.165C289.462 223.165 288.608 222.863 287.917 222.258C287.266 221.613 286.941 220.787 286.941 219.779C286.941 218.126 287.51 216.735 288.649 215.606C289.787 214.477 291.292 213.631 293.163 213.066C295.074 212.502 297.128 212.22 299.324 212.22C302.943 212.22 305.708 213.087 307.619 214.82C309.531 216.554 310.486 219.376 310.486 223.286V236.771C310.486 238.505 311.32 239.372 312.987 239.372H314.39L315 240.037C314.268 240.964 313.455 241.67 312.56 242.153C311.665 242.677 310.445 242.94 308.9 242.94C307.152 242.94 305.749 242.536 304.691 241.73C303.675 240.883 303.004 239.775 302.678 238.404C301.377 239.775 300.096 240.883 298.836 241.73C297.575 242.577 295.928 243 293.895 243ZM297.433 239.13C298.246 239.13 299.019 238.948 299.75 238.586C300.482 238.223 301.397 237.618 302.495 236.771V227.096C301.438 227.257 300.36 227.459 299.263 227.701C297.514 228.104 296.091 228.85 294.993 229.938C293.935 231.027 293.407 232.498 293.407 234.353C293.407 235.965 293.773 237.175 294.505 237.981C295.277 238.747 296.253 239.13 297.433 239.13Z"
          />
        </g>
        <rect
          className="asb-text-line-1"
          height="10"
          rx="4"
          width="200"
          x="180"
          y="271"
        />
        <rect
          className="asb-text-line-2"
          height="10"
          rx="4"
          width="200"
          x="180"
          y="293"
        />
        <rect
          className="asb-text-line-3"
          height="10"
          rx="4"
          width="115"
          x="180"
          y="315"
        />
        <rect
          className="asb-button"
          height="16"
          rx="8"
          width="80"
          x="240"
          y="349"
        />
        <circle className="asb-sun-1" cx="173" cy="460" r="9" />
        <path
          className="asb-mountains-1"
          d="M175.833 482.711L156.695 502.162C154.207 504.691 155.999 508.968 159.547 508.968H228.004C231.098 508.968 233.02 505.605 231.451 502.938L213.143 471.838C211.669 469.335 208.103 469.188 206.428 471.561L194.581 488.344C193.205 490.293 190.439 490.612 188.655 489.027L181.342 482.526C179.75 481.111 177.327 481.192 175.833 482.711Z"
        />
        <circle className="asb-sun-2" cx="347" cy="460" r="9" />
        <path
          className="asb-mountains-2"
          d="M349.833 482.711L330.695 502.162C328.207 504.691 329.999 508.968 333.547 508.968H402.004C405.098 508.968 407.02 505.605 405.451 502.938L387.143 471.838C385.669 469.335 382.103 469.188 380.428 471.561L368.581 488.344C367.205 490.293 364.439 490.612 362.655 489.027L355.342 482.526C353.75 481.111 351.327 481.192 349.833 482.711Z"
        />
      </g>
      <g className="asb-small-text-block" fill={`url(#${shimmerId})`}>
        <rect height="10" rx="4" width="96" x="145" y="455" />
        <rect height="10" rx="4" width="96" x="145" y="477" />
        <rect height="10" rx="4" width="51" x="145" y="499" />
      </g>
      <g
        className="asb-small-text-block asb-small-text-block-2"
        fill={`url(#${shimmerId})`}
      >
        <rect height="10" rx="4" width="96" x="319" y="455" />
        <rect height="10" rx="4" width="96" x="319" y="477" />
        <rect height="10" rx="4" width="51" x="319" y="499" />
      </g>
      <rect
        className="asb-text-wrapper"
        height="238"
        rx="7"
        stroke={tokens.colors.borderDefault}
        strokeWidth="2"
        width="330"
        x="115"
        y="163"
      />
      <rect
        className="asb-image-wrapper-1"
        height="126"
        rx="7"
        stroke={tokens.colors.borderDefault}
        strokeWidth="2"
        width="156"
        x="115"
        y="419"
      />
      <rect
        className="asb-image-wrapper-2"
        height="126"
        rx="7"
        stroke={tokens.colors.borderDefault}
        strokeWidth="2"
        width="156"
        x="289"
        y="419"
      />
      <rect
        className="asb-aside-wrapper"
        height="202"
        rx="7"
        stroke={tokens.colors.borderDefault}
        strokeWidth="2"
        width="140"
        x="289"
        y="180"
      />
    </svg>
  );
});

AnimatedSiteBuilderSkeleton.displayName = 'AnimatedSiteBuilderSkeleton';
